import React, { ReactElement } from 'react';
import { QueryTypeEnum } from '@components/Widgets/utils';
import { AvTag, AvTooltip } from '@fabric-ui/components';
import { Filter } from '@fabric-ui/icons';
import { dotStyle, flex, shadowInsetBorder } from '@fabric-ui/theme';
import { emptyArray } from '@fabric-ui/utils';
import { Box, LinearProgress, Skeleton, Typography, TypographyVariant } from '@mui/material';
import { WidgetDatum } from '@utils/dashboardDataUtils';
import { isNullOrUndefined } from '@utils/utils';
import { useQueriesObjectAsyncWithCachedResult } from '../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults';
import useElementOnScreen from '../../hooks/useElementOnScreen';
import useQuerySql from '../../hooks/useQuerySql';
import WidgetLoadingState from '../../views/CustomDashboards/components/Widget/WidgetHistoricLoadingState/WidgetLoadingState';
import { DwQueryRequest } from '../../views/CustomDashboards/types';
import { ErrorBoundary } from '../ErrorBoundary';
import { ErrorLoadingWidget, HistoricDataIndicator, NoDataFoundWidget, WidgetTitle } from './layout.components';

const filterIcon = <Filter strokeWidth={0.5} style={{ width: 16, height: 16 }} />;

export const chartStyle = {
  ...flex.col,
  flex: 1,
  position: 'relative',
  overflow: 'auto',
  maxHeight: '100%',
  '.recharts-surface': {
    pointerEvents: 'auto',
    height: 'auto',
    width: 'auto',
    'text:not(.custom-chart-text)': {
      fontSize: '12px',
      fill: 'var(--color-content-base-primary)',
    },
    '.recharts-xAxis': {
      transform: 'translate(0,5px)',
    },
  },
};

interface SliceIndicatorProps {
  data?: any[];
  isSlicer?: boolean;
  hideText?: boolean;
}

function SliceIndicator({ data = emptyArray, isSlicer, hideText = false }: SliceIndicatorProps) {
  if (isNullOrUndefined(isSlicer)) {
    return null;
  }
  const tooltipContent = (
    <Box sx={{ ...flex.colItemsStart }}>
      {data.map(item => (
        <Box key={item.value} sx={{ ...flex.itemsCenter, gap: 1 }}>
          <Box sx={{ ...dotStyle(item.color), border: '1px solid var(--color-border-base-secondary)' }} />
          <div>{item.value}</div>
        </Box>
      ))}
    </Box>
  );
  return (
    <AvTooltip title={isSlicer ? tooltipContent : hideText ? 'Sliced' : ''}>
      <div>
        <AvTag
          text={
            <>
              {filterIcon}
              {!hideText && (isSlicer ? 'Slicer' : 'Sliced')}
            </>
          }
          variant="primary"
          sx={{
            ...flex.itemsCenter,
            gap: '6px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        />
      </div>
    </AvTooltip>
  );
}

interface WidgetProps {
  children: ReactElement<any>;
  title?: string;
  titleVariant?: TypographyVariant;
  sql?: string | string[];
  query?: DwQueryRequest[];
  queryType?: QueryTypeEnum;
  metricKey?: string;
  metricName?: string;
  valLabel?: string;
  valKey?: string;
  height?: string | number;
  width?: string | number;
  hideTitle?: boolean;
  titleMargin?: number;
  background?: string;
  transformData?: WidgetDatum['transformData'];
  onError?: (e) => void;
  sx?: any;
  queryOptions?: any;
  sliceProps?: any;
  historicalDataProps?: any;
  errorProps?: any;
  muteErrors?: boolean;
  noDataProps?: any;
  dateFormat?: string;
  showTotalRows?: boolean;
  useNewLoaders?: boolean;
  dashboardName?: string;
  visible?: boolean;
  root?: HTMLElement | null;
}

function Widget({
  children,
  title = '',
  sql = '',
  query = emptyArray,
  queryType = QueryTypeEnum.SQL_QUERY,
  metricKey,
  metricName,
  valLabel,
  valKey,
  height = 'auto',
  width = '100%',
  hideTitle,
  titleMargin = 2,
  titleVariant,
  background,
  transformData,
  onError,
  sx,
  queryOptions = { enabled: true },
  sliceProps,
  historicalDataProps,
  errorProps,
  muteErrors,
  noDataProps,
  dateFormat = 'MMM',
  showTotalRows,
  useNewLoaders = false,
  dashboardName = '',
  visible: visibleProp,
  root,
}: WidgetProps) {
  const enableQueryObj = !!query.length && [QueryTypeEnum.OBJECT_ASYNC, QueryTypeEnum.CACHED_RESULT].includes(queryType);
  const { ref: visibilityRef, isVisible: isVisibleInternal } = useElementOnScreen({ root });
  const isVisible = visibleProp ?? isVisibleInternal;
  const {
    data: dataObj,
    totals: totalsObj,
    isLoading: isLoadingObj,
    isRefetching: isRefetchingObj,
    errors: errorsObj = emptyArray,
    asyncResult,
  } = useQueriesObjectAsyncWithCachedResult({
    origin: 'INTERACTIVE_QUERY',
    queryObject: query,
    tags: { widgetId: `${dashboardName}-${title}` },
    showTotals: showTotalRows,
    totalRowCount: true,
    options: {
      ...queryOptions,
      ...(!isVisible ? { enabled: false } : { enabled: queryOptions.enabled && enableQueryObj }),
    },
    onSuccess: transformData,
    onError,
    muteErrors,
    isCacheEnabled: enableQueryObj && queryType === QueryTypeEnum.CACHED_RESULT,
  });

  const {
    data: dataSql,
    totals: totalsSql,
    isLoading,
    isRefetching: isRefetchingSql,
    errors = emptyArray,
    isPlaceholderData,
  } = useQuerySql({
    key: title,
    sql,
    onSuccess: transformData,
    onError,
    muteErrors,
    showTotals: showTotalRows,
    options: { ...queryOptions, ...(!isVisible && { enabled: !enableQueryObj }) },
  });
  const isQueryExist = enableQueryObj || !!sql;
  const data = enableQueryObj ? dataObj : dataSql;
  const totals = enableQueryObj ? totalsObj : totalsSql;
  const isRefetching = enableQueryObj ? isRefetchingObj : isRefetchingSql;
  const loading = enableQueryObj ? isLoadingObj || isRefetching : isLoading && !isPlaceholderData;
  const isError =
    errorProps?.forceShowError ||
    (enableQueryObj ? errorsObj.length && enableQueryObj && (asyncResult as []).every(res => res === 'ASYNC_DONE') : errors.length);
  const shouldRender =
    !isQueryExist || (!loading && (useNewLoaders ? !isRefetching : true) && Object.keys(data || {})?.length > 0 && !isError);
  const isNoData = isVisible && !loading && !isError && data?.length === 0;
  const isErrorQuery = isQueryExist && !!isError;
  const isSkeleton = isNullOrUndefined(data) || loading || (useNewLoaders ? isRefetching : false);

  return (
    <Box
      ref={visibilityRef}
      data-widget-title={title}
      sx={{
        ...chartStyle,
        width,
        minHeight: loading ? Number(height) || 'auto' : height,
        p: background === 'transparent' ? 0 : 3,
        pt: background === 'transparent' ? 0 : 2,
        '&, .MuiTableCell-head': {
          background: theme => background || theme.palette.white.main,
        },
        boxShadow: background === 'transparent' ? undefined : shadowInsetBorder,
        ...sx,
        ...(isSkeleton && { pb: 3 }),
      }}>
      <ErrorBoundary fallbackComponent={<ErrorLoadingWidget {...errorProps} />}>
        {title && !hideTitle ? (
          titleVariant ? (
            <Typography sx={{ mb: titleMargin }} variant={titleVariant}>
              {title}
            </Typography>
          ) : (
            <WidgetTitle marginBottom={titleMargin}>{title}</WidgetTitle>
          )
        ) : null}
        {isNoData && <NoDataFoundWidget showImage {...noDataProps} />}
        {isErrorQuery && <ErrorLoadingWidget {...errorProps} />}
        {isRefetching && <LinearProgress sx={{ top: 0, left: 0, position: 'absolute', width: '100%', height: '2px' }} />}
        {!isErrorQuery &&
          isSkeleton &&
          (useNewLoaders ? (
            <WidgetLoadingState isAsync={enableQueryObj && historicalDataProps} sx={{ zIndex: 1, mb: 0, '> *': { textAlign: 'center' } }} />
          ) : (
            <Skeleton animation="wave" variant="rectangular" sx={{ flex: 1 }} />
          ))}
        <Box
          sx={{
            ...flex.row,
            position: 'absolute',
            right: !isNullOrUndefined(sliceProps?.isSlicer) ? 0 : '24px',
            top: '20px',
            flexDirection: 'row-reverse',
            gap: 0.5,
          }}>
          {sliceProps && (enableQueryObj ? !loading && !isRefetching : true) && <SliceIndicator {...sliceProps} />}
          {historicalDataProps && (enableQueryObj ? !loading && !isRefetching : true) && <HistoricDataIndicator {...historicalDataProps} />}
        </Box>
        {shouldRender &&
          React.cloneElement(children, {
            ...(isQueryExist && { data }),
            ...(showTotalRows && { totals }),
            isLoading: isLoading || isRefetching,
            metricKey,
            metricName,
            valLabel,
            valKey,
            sql,
            dateFormat,
          })}
      </ErrorBoundary>
    </Box>
  );
}

export default Widget;
